import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const TIMEZONE_SUFFIX_PATTERN = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_SHAPE_PATTERN = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

export const V3_DATE_ONLY_FIELDS = new Set([
    "flight_date",
    "schedule_date",
    "start_date",
    "end_date",
    "period_start",
    "period_end",
]);

const isDev = process.env.NODE_ENV === "development";

function isDayjsLike(value) {
    return Boolean(value && typeof value === "object" && value.$y !== undefined && value.$M !== undefined && value.$D !== undefined);
}

function isDateLikeObject(value) {
    return value instanceof Date || isDayjsLike(value) || value?._isAMomentObject || value?.$d;
}

export function serializeV3DateOnly(value) {
    if (!value) {
        return value;
    }
    if (typeof value === "string" && DATE_ONLY_PATTERN.test(value.trim())) {
        return value.trim();
    }
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
}

export function serializeV3Instant(value, fieldName = "datetime") {
    if (!value) {
        return value;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (DATE_ONLY_PATTERN.test(trimmed)) {
            return trimmed;
        }
        if (DATETIME_SHAPE_PATTERN.test(trimmed)) {
            if (!TIMEZONE_SUFFIX_PATTERN.test(trimmed)) {
                if (isDev) {
                    console.warn(`API v3 datetime 字段 '${fieldName}' 缺少时区信息，保持原值交由后端拒绝。`);
                }
                return value;
            }
            const parsed = dayjs(trimmed);
            return parsed.isValid() ? parsed.utc().toISOString() : value;
        }
        return value;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? value : value.toISOString();
    }
    if (isDateLikeObject(value)) {
        const parsed = dayjs.utc(value);
        return parsed.isValid() ? parsed.toISOString() : value;
    }
    return value;
}

export function serializeV3RequestDates(data) {
    if (!data || typeof data !== "object") {
        return data;
    }
    if (Array.isArray(data)) {
        return data.map(serializeV3RequestDates);
    }
    const result = { ...data };
    Object.entries(result).forEach(([key, value]) => {
        if (!value) {
            return;
        }
        if (V3_DATE_ONLY_FIELDS.has(key)) {
            result[key] = serializeV3DateOnly(value);
            return;
        }
        if (typeof value === "string" || isDateLikeObject(value)) {
            result[key] = serializeV3Instant(value, key);
            return;
        }
        if (typeof value === "object") {
            result[key] = serializeV3RequestDates(value);
        }
    });
    return result;
}

export function normalizeV3ResponseDates(data) {
    if (!data || typeof data !== "object") {
        return data;
    }
    if (Array.isArray(data)) {
        return data.map(normalizeV3ResponseDates);
    }
    const result = { ...data };
    Object.entries(result).forEach(([key, value]) => {
        if (!value) {
            return;
        }
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (DATE_ONLY_PATTERN.test(trimmed)) {
                result[key] = trimmed;
                return;
            }
            if (DATETIME_SHAPE_PATTERN.test(trimmed)) {
                if (!TIMEZONE_SUFFIX_PATTERN.test(trimmed)) {
                    if (isDev) {
                        console.warn(`API v3 response datetime 字段 '${key}' 缺少时区信息，保持原值。`);
                    }
                    return;
                }
                const parsed = dayjs(trimmed);
                if (parsed.isValid()) {
                    result[key] = parsed.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
                return;
            }
        }
        if (typeof value === "object") {
            result[key] = normalizeV3ResponseDates(value);
        }
    });
    return result;
}
